import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { entitiesPropsFactory, getActiveId, getAllEntities, getEntity, selectActiveEntity, selectAllEntities, selectEntity, selectMany, setActiveId, setEntities, updateEntities, withActiveId, withEntities } from '@ngneat/elf-entities';
import { selectIsRequestPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { catchError, combineLatestWith, from, Observable, of, Subscription, switchMap, take, tap, throwError } from 'rxjs';
import { delayWhen, distinctUntilChanged, filter, map } from 'rxjs/operators';

import { MenuPrice } from '../../api/v1/models/menu-price';
import { MenuDto } from '../../api/v1/models/menu-dto';
import { MenuCategoryDto } from '../../api/v1/models/menu-category-dto';
import { MenuItemDto } from '../../api/v1/models/menu-item-dto';
import { MenuItemSizeDto } from '../../api/v1/models/menu-item-size-dto';
import { MenuService } from '../../api/v1/services/menu.service';
import { SeoService } from '../../services/seo.service';
import { StatusMessageService } from '../services/status-message.service';
import { ConfigRepository } from './config.repository';
import { UIMenuItemDto } from '../interfaces/ui-menu-item-dto.interface';
import { Location } from '@angular/common';
import { RelatedProduct } from '../../api/v1/models/related-product';
import { PastOrderItemDto } from '../../api/v1/models/past-order-item-dto';
import { getCurrentDayName } from '../utils/get-time-intervals';
import { UIMenuCategoryDto } from '../interfaces/ui-menu-category-dto.interface';
import { MenuScheduleDto } from '../../api/v1/models/menu-schedule-dto';
import { Allergen, ItemModifierGroup, Label } from '../../api/v1/models';
import { PriceCalculationService } from '../services/price-calculation.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

interface ModifierGroupPrice {
  minimalPrice: number,
  isNullablePrice: boolean,
}

interface ModifierPrice {
  price: number,
  minCount: number,
  maxCount: number,
}

export interface MenuListModel {
  stopList: Map<string, number>,
  itemsSlugToIdMap: { [key: string]: { [key: string]: string } } | null
  categorySlugToIdMap: Map<string, string> | null
  itemIdToUniqKeys: { [key: string]: string[] } | null
  searchString: string | null,
  unavailableCategories: string[],
  isMenuLoad: boolean,
}

const {itemsEntitiesRef, withItemsEntities} = entitiesPropsFactory('items');

const menuStore = createStore({name: 'menu'},
  withProps<MenuListModel>({
    stopList: new Map<string, number>(),
    itemsSlugToIdMap: null,
    categorySlugToIdMap: null,
    itemIdToUniqKeys: null,
    searchString: null,
    unavailableCategories: [],
    isMenuLoad: false,
  }),
  withEntities<UIMenuCategoryDto>(),
  withActiveId(),
  withItemsEntities<UIMenuItemDto, 'uniqKey'>({idKey: 'uniqKey'}),
  withRequestsStatus<'isLoadingMenu'>(),
);

@Injectable({providedIn: 'root'})
export class MenuListRepository {
  private configRepository = inject(ConfigRepository);
  private api = inject(MenuService);
  private statusMessage = inject(StatusMessageService);
  private menuService = inject(MenuService);
  private seoService = inject(SeoService);
  private router = inject(Router);
  private location = inject(Location);
  private priceCalculationService: PriceCalculationService = inject(PriceCalculationService);

  constructor() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
  }

  isMenuLoad$: Observable<boolean> = menuStore.pipe(select(state => state.isMenuLoad));
  searchString$: Observable<string | null> = menuStore.pipe(select(state => state.searchString));

  categories$: Observable<UIMenuCategoryDto[] | null> = menuStore.pipe(selectAllEntities());
  categoryItems$: Observable<UIMenuItemDto[] | null> = menuStore.pipe(selectAllEntities({ref: itemsEntitiesRef}));
  menuNavCategories$: Observable<UIMenuCategoryDto[] | null> = menuStore.pipe(
    select(state => state.unavailableCategories),
    combineLatestWith(this.categories$),
    map(([unavailableIds, categories]) => {
      if(!categories) {
        return null;
      }

      const mapped = categories.map((category, i) => {
        category.isScheduleUnavailable = unavailableIds.includes(category.id);
        return {index: i, value: category.isScheduleUnavailable ? 1 : -1};
      });

      mapped.sort((a, b) => {
        return a.value - b.value;
      });

      return mapped.map((item => categories[item.index]));
    }),
    map((categories: UIMenuCategoryDto[] | null) => {
      if(!categories) {
        return null;
      }
      categories.forEach(category => {
        category.currentAvailableRange = this.getAvailableRange(category.schedule);

        if(category.buttonImage && category.buttonImage['src']) {
          return;
        }

        for(let i = 0; i < category.itemsIDs.length; i++) {
          const item = menuStore.query(getEntity(category.itemsIDs[i], {ref: itemsEntitiesRef}));
          if(item?.itemSizes[0]?.buttonImage?.['src']) {
            category.buttonImage = item.itemSizes[0].buttonImage;
            break;
          }
        }
      });
      return categories;
    })
  );

  public activeCategory$: Observable<UIMenuCategoryDto> = menuStore.pipe(
    selectActiveEntity(),
    filter((category): category is UIMenuCategoryDto => !!category),
    tap(data => {
      this.seoService.setDynamicDataMetaTags(data.name, data.description);
    })
  );

  activeCategorySchedule$: Observable<MenuScheduleDto | null> = menuStore.pipe(
    selectActiveEntity(),
    filter((category): category is UIMenuCategoryDto => !!category),
    map(category => this.getAvailableRange(category.schedule)),
    map(schedule => {
      if(schedule) {
        schedule.from = schedule.from.slice(0, 5);
        schedule.to = schedule.to.slice(0, 5);
      }
      return schedule;
    })
  );

  public activeCategoryId$: Observable<string> = this.activeCategory$.pipe(map(category => category.id || ''));

  activeCategoryIndex$: Observable<number> = this.menuNavCategories$.pipe(
    filter((categories): categories is UIMenuCategoryDto[] => !!categories),
    combineLatestWith(this.activeCategoryId$),
    map(([categories, activeCategoryId]) => {
      for(let i = 0; i < categories.length; i++) {
        if(categories[i].id === activeCategoryId) {
          return i;
        }
      }
      return 0;
    })
  );

  public activeCategoryItems$: Observable<UIMenuItemDto[]> = this.activeCategory$.pipe(
    switchMap(category => this.getItemsByUniqKey(category!.itemsIDs)),
    filter((items): items is UIMenuItemDto[] => !!items),
    map(items => this.filterNullPrice(items)),
    map(items => this.sortMenuItems(items)),
  );

  menuNavCategoryItems$: Observable<UIMenuItemDto[]> = this.menuNavCategories$.pipe(
    filter((categories): categories is UIMenuCategoryDto[] => !!categories),
    combineLatestWith(this.configRepository.activeOutletId$.pipe(distinctUntilChanged())),
    switchMap(([categories]) =>
      this.categoryItemsByUniqKey(categories.reduce((arr, category) => arr.concat(category.itemsIDs), [] as string[]))
    ),
  );

  scheduleAvailableCategoryItems$: Observable<UIMenuItemDto[]> = this.menuNavCategories$.pipe(
    filter((categories): categories is UIMenuCategoryDto[] => !!categories),
    map(categories => categories.filter(category => !category.isScheduleUnavailable)),
    switchMap(categories =>
      this.categoryItemsByUniqKey(categories.reduce((arr, category) => arr.concat(category.itemsIDs), [] as string[]))
    ),
  );

  getSearchedItems(searchString: string): Observable<UIMenuItemDto[]> {
    return menuStore.pipe(selectAllEntities({ref: itemsEntitiesRef})).pipe(
      map(items => {
        return items.filter(item => {
          return item.name.toLowerCase().includes(searchString.toLowerCase());
        });
      })
    );
  }

  showItems$: Observable<UIMenuItemDto[] | null> = this.searchString$.pipe(
    switchMap(searchString => {
      if(searchString) {
        return this.getSearchedItems(searchString);
      }
      return this.menuNavCategoryItems$;
    })
  );

  public loadingMenuStatus$: Observable<boolean> = menuStore.pipe(selectIsRequestPending('isLoadingMenu'));

  public stopListItems$: Observable<Map<string, number>> = menuStore.pipe(select(state => state.stopList));

  labelList$ = this.scheduleAvailableCategoryItems$.pipe(
    map(items => items?.reduce((labels: Label[], item) => [...labels, ...item.labels], [])),
    map(items => items?.map(i => i.name)),
    map(labels => Array.from(new Set(labels)))
  );
  allergenList$ = this.scheduleAvailableCategoryItems$.pipe(
    map(items => items?.reduce((allergens: Allergen[], item) => [...allergens, ...item.allergens], [])),
    map(items => items?.map(i => i.name)),
    map(allergens => Array.from(new Set(allergens)))
  );

  get activeCategoryId(): string {
    return menuStore.query(getActiveId);
  }

  categoryItemsByUniqKey(keys: string[]): Observable<UIMenuItemDto[]> {
    return this.getItemsByUniqKey(keys).pipe(
      filter((items): items is UIMenuItemDto[] => !!items),
      map(items => this.filterNullPrice(items)),
      map(items => this.sortMenuItems(items)),
    );
  }

  private getItemsByUniqKey(keys: string[]): Observable<UIMenuItemDto[]> {
    return menuStore.pipe(selectMany(keys, {ref: itemsEntitiesRef}));
  }

  fetchMenu(clearMenuFetchPromise = false): void {
    if(clearMenuFetchPromise && typeof Worker !== 'undefined' && typeof window !== 'undefined') {
      (window as any)['menuFetchPromise'] = null;
    }

    this.fetchMenuList().pipe(
      switchMap(() => this.configRepository.activeOutletId$),
      filter(storeId => !!storeId),
      switchMap(() => this.fetchStopList())
    ).subscribe();
  }

  private getMenuObservable(): Observable<MenuDto> {
    if(typeof Worker !== 'undefined' && typeof window !== 'undefined' && (window as any)['menuFetchPromise']) {
      return from((window as any)['menuFetchPromise']) as Observable<MenuDto>;
    }
    return this.api.apiV1MenuGet$Json().pipe(
      map(response => {
        if(response.error || !response.result) {
          throw response;
        }
        return response.result;
      })
    );
  }

  fetchMenuList(): Observable<MenuDto> {
    menuStore.update(updateRequestStatus('isLoadingMenu', 'pending'));
    return this.getMenuObservable().pipe(
      map((menu => {
        if('error' in menu) {
          throw new Error('menu is not found');
        }

        return menu;
      })),
      delayWhen(() => this.configRepository.activeOutlet$),
      tap(data => {
        menuStore.update(updateRequestStatus('isLoadingMenu', 'success'));

        if(!data.itemCategories) {
          return;
        }
        this.setList(data.itemCategories);
        this.setActiveCatalogUrl();
      }),

      catchError(error => {
        this.statusMessage.setStatusMessage({
          title: $localize`:@@MessageModalTitleError:Error`,
          message: $localize`:@@FailedToGetMenu:Failed to get menu. Reload the page or try again later.`,
          type: 'error'
        });

        menuStore.update(updateRequestStatus('isLoadingMenu', 'error', error));

        return throwError(error);
      })
    );
  }

  private getPriceByOutlet(prices: MenuPrice[]): number | null {
    const activeOutletId = this.configRepository.activeOutletId;
    return this.priceCalculationService.getPriceByOutletId(prices, activeOutletId);
  }

  getItemMinimalPrice(item: MenuItemDto): number | null {
    const defaultSize = this.getDefaultSize(item);
    const defaultPrice = this.getDefaultPrice(defaultSize);

    if(defaultPrice !== 0) {
      return null;
    }

    const modifierGroupPrice: ModifierGroupPrice[] = [];
    let minimalPrice: number = 0;

    /**
     *  Подсчет минимальной стоимости для блюда, если его основная стоимость === 0
     *  В зависимости от флага группы модификаторов childModifiersHaveMinMaxRestrictions,
     *  проверяем ограничение группы(false) или ограничения самих модификаторов(true).
     *
     *  Т.к. блюда с нулевой ценой не может быть в корзине,
     *  то находим минимальную стоимость(исключаем нулевые цены) каждой группы
     *  и запоминаем имеет ли она минимальную нулевую стоимость.
     */
    defaultSize.itemModifierGroups.forEach(modifierGroup => {
      if(modifierGroup.childModifiersHaveMinMaxRestrictions) {
        modifierGroupPrice.push(this.getMinimalGroupPriceByChild(modifierGroup));
        return;
      }
      modifierGroupPrice.push(this.getMinimalGroupPrice(modifierGroup));
    });

    /**
     * Далее сортируем группы по минимальным ценам
     * и добавляем цену в общую, если уже имеем не нулевую минимальную стоимость
     * и группа не имеет нулевой стоимости.
     *
     */
    modifierGroupPrice.sort((a, b) => a.minimalPrice - b.minimalPrice);

    modifierGroupPrice.forEach(group => {
      if(!minimalPrice) {
        minimalPrice += group.minimalPrice;
        return;
      }

      if(!group.isNullablePrice) {
        minimalPrice += group.minimalPrice;
      }
    });

    return minimalPrice;
  }

  private getMinimalGroupPriceByChild(modifierGroup: ItemModifierGroup): ModifierGroupPrice {
    let minimalPrice: number = 0;
    let modifierCount = modifierGroup.restrictions.minQuantity;

    const modifierPrices: ModifierPrice[] = [];
    let isNullablePrice: boolean = false;
    let nullablePriceMaxCount: number = 0;

    modifierGroup.items.forEach(modifier => {
      const price = (this.getPriceByOutlet(modifier.prices) || 0);

      if(!price) {
        nullablePriceMaxCount += modifier.restrictions.maxQuantity;
        return;
      }

      nullablePriceMaxCount += modifier.restrictions.freeQuantity;

      if(modifier.restrictions.freeQuantity >= modifier.restrictions.maxQuantity) {
        return;
      }

      modifierPrices.push({
        price,
        minCount: modifier.restrictions.minQuantity,
        maxCount: modifier.restrictions.maxQuantity - modifier.restrictions.freeQuantity,
      });
    });

    modifierPrices.sort((a, b) => a.price - b.price);

    if(nullablePriceMaxCount >= modifierCount) {
      modifierCount = 1;
      isNullablePrice = true;
    }
    else {
      modifierCount -= nullablePriceMaxCount;
    }

    for(let i = 0; i < modifierPrices.length; i++) {
      if(modifierCount <= 0) {
        break;
      }

      const currentCount = Math.min(modifierPrices[i].maxCount, modifierCount);
      const price = modifierPrices[i].price * currentCount;

      modifierCount -= currentCount;
      minimalPrice += price;
    }

    return {
      isNullablePrice: isNullablePrice,
      minimalPrice: minimalPrice,
    };
  }

  private getMinimalGroupPrice(modifierGroup: ItemModifierGroup): ModifierGroupPrice {
    let minimalPrice: number = 0;
    let isNullablePrice: boolean = false;

    modifierGroup.items.forEach(modifier => {
      const price = (this.getPriceByOutlet(modifier.prices) || 0) * modifierGroup.restrictions.minQuantity;

      if(!price) {
        isNullablePrice = true;
        return;
      }

      if(minimalPrice && price >= minimalPrice) {
        return;
      }

      minimalPrice = price;
    });

    return {
      isNullablePrice: isNullablePrice,
      minimalPrice: minimalPrice
    };
  }

  private setList(menuList: MenuCategoryDto[]): void {
    const UIMenuList: UIMenuCategoryDto[] = [];
    const unavailableCategories: string[] = [];
    const menuItems: UIMenuItemDto[] = [];

    const itemsSlugMap: { [key: string]: { [key: string]: string } } = {};
    const categorySlugMap: Map<string, string> = new Map<string, string>();
    const itemIdToUniqKeys: { [key: string]: string[] } = {};

    menuList.forEach(category => {
      if(category.isHidden) {
        return;
      }

      if(!this.checkCategoryAvailable(category)) {
        unavailableCategories.push(category.id);
      }
      else {
        categorySlugMap.set(category.slug, category.id);
      }

      const itemsIDs: string[] = [];
      const categoryItemsSlug: { [key: string]: string } = {};

      category.items?.forEach(item => {
        if(item.isHidden) {
          return;
        }

        item.itemSizes = item.itemSizes.filter(size => !size.isHidden);
        if(!item.itemSizes.length) {
          return;
        }

        const key = category.id + item.itemId;

        if(!itemIdToUniqKeys[item.itemId]) {
          itemIdToUniqKeys[item.itemId] = [];
        }
        itemIdToUniqKeys[item.itemId].push(key);

        itemsIDs.push(key);

        const uiMenuItem: UIMenuItemDto = {
          ...item,
          uniqKey: key,
          categoryId: category.id,
          categorySlug: category.slug,
        };

        const minimalPrice = this.getItemMinimalPrice(item);

        if(minimalPrice) {
          uiMenuItem.minimalPrice = minimalPrice;
        }

        menuItems.push(uiMenuItem);

        categoryItemsSlug[item.slug] = key;
      });

      itemsSlugMap[category.id] = categoryItemsSlug;
      UIMenuList.push({
        ...category,
        itemsIDs: itemsIDs,
      });
    });

    menuStore.update(
      setEntities(UIMenuList),
      setEntities(menuItems, {ref: itemsEntitiesRef}),
      state => ({
        ...state,
        categorySlugToIdMap: categorySlugMap,
        itemsSlugToIdMap: itemsSlugMap,
        itemIdToUniqKeys: itemIdToUniqKeys,
        unavailableCategories: unavailableCategories,
        isMenuLoad: true,
      }),
      setActiveId(UIMenuList.length ? UIMenuList[0].id : null)
    );
  }

  private checkCategoryAvailable(category: MenuCategoryDto, deliveryDateTime: string | null = null): boolean {
    if(!category.schedule) {
      return true;
    }
    return !!this.getAvailableRange(category.schedule, deliveryDateTime);
  }

  private getAvailableRange(schedule: MenuCategoryDto['schedule'], deliveryTime: string | null = null): MenuScheduleDto | null {
    const checkTime = deliveryTime
      ? dayjs(deliveryTime).tz(this.configRepository.restoTimeZone)
      : dayjs().tz(this.configRepository.restoTimeZone);

    const checkTimeMinute = checkTime.hour() * 60 + checkTime.minute();

    const daySchedule = schedule?.[getCurrentDayName(checkTime.toDate())];
    if(!daySchedule) {
      return null;
    }

    return daySchedule.find(range => this.checkTimeToRange(range.from, range.to, checkTimeMinute)) ?? null;
  }

  private checkTimeToRange(from: string, to: string, currentTime: number): boolean {
    const [startHour, startMinute] = from.split(':').map(Number);
    const [endHour, endMinute] = to.split(':').map(Number);

    const start = startHour * 60 + startMinute;
    const end = endHour * 60 + endMinute;

    // Диапазон без перехода через полночь
    if(start <= end) {
      return currentTime >= start && currentTime <= end;
    }

    // Диапазон пересекает полночь
    return currentTime >= start || currentTime <= end;
  }

  isCategoryScheduleAvailable(categoryId: string, deliveryDateTime: string | null = null): boolean {
    const category = menuStore.query(getEntity(categoryId));
    if(!category) {
      return false;
    }
    return this.checkCategoryAvailable(category, deliveryDateTime);
  }

  getItem(uniqKey: string): UIMenuItemDto | undefined {
    return menuStore.query(getEntity(uniqKey, {ref: itemsEntitiesRef}));
  }

  getItem$(uniqKey: string): Observable<UIMenuItemDto | undefined> {
    return menuStore.pipe(selectEntity(uniqKey, {ref: itemsEntitiesRef}));
  }

  getItemBySlugFromCategory(slug: string, categoryId: string | null = null): Observable<UIMenuItemDto> {
    const categoryId$ = categoryId ? of(categoryId) : this.activeCategoryId$;
    return categoryId$.pipe(
      switchMap(categoryId => {
        return menuStore.pipe(
          select(state => state.itemsSlugToIdMap),
          filter(itemsSlugToIdMap => !!itemsSlugToIdMap),
          map(itemsSlugToIdMap => itemsSlugToIdMap![categoryId])
        );
      }),
      filter(itemsMap => !!itemsMap),
      filter(itemsMap => !!itemsMap[slug]),
      switchMap(itemsMap => {
        return this.getItem$(itemsMap![slug]);
      }),
      filter((item): item is UIMenuItemDto => !!item)
    );
  }

  getItemBySizeSku(sku: string): { item: UIMenuItemDto, size: MenuItemSizeDto } | null {
    let findItem;
    let findSize;
    const items = menuStore.query(getAllEntities({ref: itemsEntitiesRef}));

    items.forEach(item => {
      item.itemSizes.forEach(size => {
        if(size.sku == sku) {
          findItem = item;
          findSize = size;
        }
      });
    });

    return findSize && findItem ? {item: findItem, size: findSize} : null;
  }

  setActiveCategory(id: string): void {
    menuStore.update(setActiveId(id));
  }

  setActiveCategoryBySlug(slug: string): void {
    const categoryMap = menuStore.query(state => state.categorySlugToIdMap);
    if(!categoryMap) {
      return;
    }
    let categoryId = categoryMap.get(slug);

    /*
    * Если нет категории с текущим slug, то берем первую в списке и редеректим туда
    * */
    if(!categoryId) {
      categoryId = categoryMap.entries().next().value?.[1];
    }

    if(!categoryId) {
      return;
    }

    this.setActiveCategory(categoryId);
  }

  fetchStopList(): Observable<{ [p: string]: number }> {
    const storeId = this.configRepository.activeOutletId;
    return this.menuService.apiV1MenuStopListsStoreIdGet$Json({storeId}).pipe(
      map((response => {
        if(response.error || !response.result) {
          throw response;
        }
        return response.result;
      })),
      tap(stopList => {
        const itemIdToUniqKeys = menuStore.query(state => state.itemIdToUniqKeys);
        if(!stopList || !itemIdToUniqKeys) {
          return;
        }

        Object.keys(stopList).forEach(key => {
          if(!itemIdToUniqKeys[key]) {
            return;
          }
          itemIdToUniqKeys[key].forEach(uniqKey => {
            menuStore.update(updateEntities(uniqKey, {balance: stopList[key]},
              {ref: itemsEntitiesRef})
            );
          });
        });
      }),
      tap(stopList => {
        if(!stopList) {
          return;
        }

        const stopListMap = new Map(Object.entries(stopList));
        if(stopListMap.size <= 0) {
          return;
        }

        menuStore.update(state => ({
          ...state,
          stopList: stopListMap
        }));
      })
    );
  }

  setActiveCatalogUrl(): void {
    if(this.location.path() !== '') {
      return;
    }
    const categoryId = menuStore.query(getActiveId);
    if(!categoryId) {
      return;
    }
    void this.router.navigate(['/category', menuStore.query(getEntity(categoryId))?.slug]);
  }

  private redirectSub: Subscription;

  redirectToActiveCategory(): void {
    this.redirectSub?.unsubscribe();

    this.redirectSub = menuStore.pipe(selectAllEntities()).pipe(
      filter(entities => !!entities.length),
      take(1),
    ).subscribe(entities => {
      void this.router.navigate(['/category', entities[0].slug]);
    });
  }

  getDefaultSize(item: MenuItemDto): MenuItemSizeDto {
    return item.itemSizes.find((size: MenuItemSizeDto) => size.isDefault) ?? item.itemSizes[0];
  }

  getDefaultPrice(currentSize: MenuItemSizeDto): number | null {
    const activeOutletId = this.configRepository.activeOutletId;

    return currentSize.prices.find((price: MenuPrice) => price.storeId === activeOutletId)?.price ?? null;
  }

  getDefaultPriceObservable(currentSize: MenuItemSizeDto): Observable<number | null> {
    return this.configRepository.activeOutletId$.pipe(
      map(activeOutletId =>
        currentSize.prices.find((price: MenuPrice) => price.storeId === activeOutletId)?.price ?? null
      )
    );
  }

  private sortMenuItems(items: UIMenuItemDto[]): UIMenuItemDto[] {
    const mapped = items.map((item, i) => {
      return {index: i, value: item.balance === 0 ? 1 : -1};
    });

    mapped.sort((a, b) => {
      return a.value - b.value;
    });

    return mapped.map((item => items[item.index]));
  }

  private filterNullPrice(items: UIMenuItemDto[]): UIMenuItemDto[] {
    return items?.filter((item: UIMenuItemDto) => {
      const defaultSize = this.getDefaultSize(item);
      return this.getDefaultPrice(defaultSize) !== null;
    });
  }

  setSearchString(value: string | null): void {
    menuStore.update(setProp('searchString', value));
  }

  getItemImagesBySku(orderItems: PastOrderItemDto): { [key: string]: string } {
    let imagesSet: { [key: string]: string } = {};
    const menuItems = menuStore.query(getAllEntities({ref: itemsEntitiesRef}));

    menuItems.forEach(item => {
      if(orderItems.sku.includes(item.sku)) {
        item.itemSizes.forEach(size => {
          if(orderItems.sku.includes(size.sku)) {
            size.prices.forEach(price => {
              if(orderItems.fullUnitPrice === (price.price * orderItems.quantity) && size.buttonImage['src']) {
                imagesSet = size.buttonImage;
              }
            });
          }
        });
      }
    });

    return imagesSet;
  }

  getRelatedProducts(relatedProducts: RelatedProduct[]): Observable<UIMenuItemDto[]> {
    let keys: string[] = [];

    relatedProducts.forEach(relates => {
      if(!this.isCategoryScheduleAvailable(relates.menuCategoryId)) {
        return;
      }
      keys.push(relates.menuCategoryId + relates.menuItemId);
    });

    keys = [...new Set([...keys])];

    return this.getItemsByUniqKey(keys);
  }
}
